import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import {
  HiMenu,
  HiSearch,
  HiPlus,
  HiChevronLeft,
  HiChevronRight,
  HiInformationCircle,
} from 'react-icons/hi';
import { MdDelete, MdEdit } from 'react-icons/md';
import Toast from '../../components/toaster';
import {
  fetchGiftCardList,
  createGiftCard,
  updateGiftCard,
  deleteGiftCard,
} from '../../services/giftCardService';
import { uploadImage } from '../../services/assetService';

const PAGE_SIZE = 20;

const giftCardCache = {
  firstLoad: true,
  lastSearch: '',
  data: null,
  totalCount: 0,
  page: 1,
};

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const FullPageSpinner = () => (
  <div className="fixed inset-0 ml-0 lg:ml-64 flex items-center justify-center z-40">
    <div className="flex items-center gap-3" role="status" aria-live="polite">
      <svg className="w-12 h-12 animate-spin" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="gc-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E9D5FF" />
            <stop offset="50%" stopColor="#A855F7" />
            <stop offset="100%" stopColor="#6B21A8" />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="40" stroke="url(#gc-gradient)" strokeWidth="6" strokeLinecap="round" strokeDasharray="60 200" fill="none" transform="rotate(-90 50 50)" />
      </svg>
    </div>
  </div>
);

const StatusBadge = ({ isActive }) => (
  <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${isActive ? 'bg-[#9FF7C9]/30 text-[#60BC7C]' : 'bg-[#FFF0F0] text-[#F76464]'}`}>
    {isActive ? 'Active' : 'Inactive'}
  </span>
);

const GiftCards = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => giftCardCache.lastSearch || '');
  const [debouncedSearch, setDebouncedSearch] = useState(() => giftCardCache.lastSearch || '');
  const [currentPage, setCurrentPage] = useState(() => giftCardCache.page || 1);
  const [data, setData] = useState(() => Array.isArray(giftCardCache.data) ? giftCardCache.data : []);
  const [totalCount, setTotalCount] = useState(() => giftCardCache.totalCount || 0);
  const [isLoading, setIsLoading] = useState(() => giftCardCache.firstLoad);
  const [listMutationLoading, setListMutationLoading] = useState(false);
  const [pageChangeLoading, setPageChangeLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', image: '', is_active: true });
  const [formErrors, setFormErrors] = useState({});
  const [formGeneralError, setFormGeneralError] = useState('');
  const [saving, setSaving] = useState(false);

  // Image source toggle ('url' or 'file') — shared for add & edit since it's one modal
  const [imageSource, setImageSource] = useState('url');
  const [imageFile, setImageFile] = useState(null);
  const [imageUploadedUrl, setImageUploadedUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');
  // Keeps the existing image when toggling mode in edit, so toggling back restores it
  const [originalImage, setOriginalImage] = useState('');

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const searchTimerRef = useRef(null);
  const listRequestIdRef = useRef(0);
  const isInitialDebounceRef = useRef(true);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const getPaginationRange = () => {
    if (totalPages === 0) return [];
    const totalBlocks = 7;
    if (totalPages <= totalBlocks) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const left = Math.max(currentPage - 1, 1);
    const right = Math.min(currentPage + 1, totalPages);
    const showLeftDots = left > 2;
    const showRightDots = right < totalPages - 1;
    const pages = [1];
    if (showLeftDots) pages.push('dots');
    for (let p = (showLeftDots ? left : 2); p <= (showRightDots ? right : totalPages - 1); p++) pages.push(p);
    if (showRightDots) pages.push('dots');
    pages.push(totalPages);
    return pages;
  };

  const loadData = useCallback(async (searchText = '', page = 1) => {
    const reqId = ++listRequestIdRef.current;
    if (giftCardCache.firstLoad) setIsLoading(true);
    setError(null);
    try {
      const res = await fetchGiftCardList(page, PAGE_SIZE, searchText);
      if (reqId !== listRequestIdRef.current) return;
      const items = Array.isArray(res?.data) ? res.data : [];
      const total = res?.totalCount ?? items.length;
      setData(items);
      setTotalCount(total);
      giftCardCache.firstLoad = false;
      giftCardCache.lastSearch = searchText;
      giftCardCache.data = items;
      giftCardCache.totalCount = total;
      giftCardCache.page = page;
      if (items.length === 0 && page > 1) setCurrentPage(1);
    } catch (err) {
      if (reqId !== listRequestIdRef.current) return;
      setError(err.message || 'Error loading data.');
      setData([]);
      setTotalCount(0);
      giftCardCache.firstLoad = false;
    } finally {
      if (reqId === listRequestIdRef.current) {
        setIsLoading(false);
        setPageChangeLoading(false);
        setListMutationLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (isInitialDebounceRef.current) { isInitialDebounceRef.current = false; return; }
    searchTimerRef.current = setTimeout(() => {
      setPageChangeLoading(true);
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  useEffect(() => { loadData(debouncedSearch, currentPage); }, [debouncedSearch, currentPage, loadData]);

  const changePage = (next) => {
    setCurrentPage(prev => {
      const n = typeof next === 'function' ? next(prev) : next;
      if (n === prev) return prev;
      queueMicrotask(() => setPageChangeLoading(true));
      return n;
    });
  };

  const resetImageState = () => {
    setImageSource('url');
    setImageFile(null);
    setImageUploadedUrl('');
    setUploadingImage(false);
    setImageUploadError('');
    setOriginalImage('');
  };

  const openAdd = () => {
    setFormData({ title: '', description: '', image: '', is_active: true });
    setFormErrors({});
    setFormGeneralError('');
    setSelectedItem(null);
    setIsEditing(false);
    resetImageState();
    setShowFormModal(true);
  };

  const openEdit = (item) => {
    setFormData({ title: item.title, description: item.description || '', image: item.image, is_active: item.is_active ?? true });
    setFormErrors({});
    setFormGeneralError('');
    setSelectedItem(item);
    setIsEditing(true);
    resetImageState();
    setOriginalImage(item.image || '');
    setShowFormModal(true);
  };

  const closeForm = () => {
    setShowFormModal(false);
    setFormData({ title: '', description: '', image: '', is_active: true });
    setFormErrors({});
    setFormGeneralError('');
    setSelectedItem(null);
    setIsEditing(false);
    resetImageState();
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    setImageUploadError('');
    if (formErrors.image) setFormErrors(prev => ({ ...prev, image: '' }));
    try {
      const uploadedUrl = await uploadImage(file);
      setImageFile(file);
      setImageUploadedUrl(uploadedUrl);
      setFormData(prev => ({ ...prev, image: uploadedUrl }));
      setOriginalImage(uploadedUrl);
    } catch (err) {
      setImageUploadError(err.message || 'Failed to upload image. Please try again.');
      setImageFile(null);
      setImageUploadedUrl('');
      setFormData(prev => ({ ...prev, image: '' }));
    } finally {
      setUploadingImage(false);
    }
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.title.trim()) errs.title = 'Title is required';
    if (!formData.image.trim()) errs.image = 'Image is required';
    return errs;
  };

  const handleSave = async () => {
    const errs = validateForm();
    if (Object.keys(errs).length) { setFormErrors(errs); return; }

    setSaving(true);
    setFormErrors({});
    setFormGeneralError('');
    try {
      if (isEditing) {
        await updateGiftCard({
          gift_card_id: selectedItem._id,
          title: formData.title,
          description: formData.description || null,
          image: formData.image,
          is_active: formData.is_active,
        });
        Toast.success('Gift card updated successfully');
      } else {
        await createGiftCard({
          title: formData.title,
          description: formData.description || null,
          image: formData.image,
          is_active: formData.is_active,
        });
        Toast.success('Gift card created successfully');
      }
      closeForm();
      setListMutationLoading(true);
      await loadData(debouncedSearch, isEditing ? currentPage : 1);
      if (!isEditing) setCurrentPage(1);
    } catch (err) {
      setFormGeneralError(err.message || 'Failed to save gift card');
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (item) => { setItemToDelete(item); setShowDeleteModal(true); };
  const closeDelete = () => { setShowDeleteModal(false); setItemToDelete(null); };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await deleteGiftCard(itemToDelete._id);
      Toast.success('Gift card deleted successfully');
      setShowDeleteModal(false);
      setItemToDelete(null);
      setListMutationLoading(true);
      await loadData(debouncedSearch, currentPage);
    } catch (err) {
      Toast.error(err.message || 'Failed to delete gift card');
    } finally {
      setDeleting(false);
    }
  };

  const showLoading = isLoading || listMutationLoading || pageChangeLoading;

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">

          {/* Header */}
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors">
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <h1 className="text-xl md:text-3xl font-bold text-gray-800">Gift Cards</h1>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="mb-4 md:mb-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-3 md:gap-4">
              <div className="w-full md:w-64">
                <div className="relative">
                  <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search title, description..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 md:py-1.5 text-sm border border-[#ECECEC] rounded-xl focus:outline-none bg-white"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 w-full md:w-auto">
                <button onClick={openAdd} className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap">
                  <HiPlus className="w-5 h-5" />
                  Add Gift Card
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          {showLoading ? (
            <FullPageSpinner />
          ) : totalCount === 0 && !error ? (
            <div className="fixed inset-0 ml-0 lg:ml-64 z-30 flex items-center justify-center pointer-events-none">
              <div className="text-center text-gray-500 pointer-events-auto">
                <div className="mb-6">
                  <svg className="w-24 h-24 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No gift cards yet</h3>
                <p className="text-sm text-gray-500 mb-6">Start by adding the first gift card</p>
                <button onClick={openAdd} className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md bg-brand hover:opacity-90">
                  <HiPlus className="w-4 h-4" /> Add Gift Card
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col flex-1 min-h-0">
              {totalCount > 0 && (
                <div className="px-4 py-2 border-b border-gray-100 flex items-center">
                  <span className="text-xs text-gray-500">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
                  </span>
                </div>
              )}

              {error ? (
                <div className="px-4 py-6 text-center text-red-500">{error}</div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">Image</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Title</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Description</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">Status</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.map(item => (
                        <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3">
                            {item.image ? (
                              <img src={item.image} alt={item.title} className="w-10 h-10 rounded-lg object-cover bg-gray-100" />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-800 font-medium">{item.title}</td>
                          <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
                            <span title={item.description}>
                              {item.description
                                ? item.description.length > 60 ? `${item.description.slice(0, 60)}…` : item.description
                                : <span className="text-gray-400 italic">—</span>
                              }
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge isActive={item.is_active} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg hover:opacity-90" style={{ backgroundColor: '#F2E2F7' }} title="Edit">
                                <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                              </button>
                              <button onClick={() => openDelete(item)} className="bg-[#FDDDDD] text-[#F76464] p-1.5 rounded-lg transition-colors" title="Delete">
                                <MdDelete className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 0 && (
                <div className="px-4 md:px-6 py-4 flex justify-end border-t border-gray-200 flex-shrink-0">
                  <div className="flex items-center gap-1">
                    <button onClick={() => changePage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                      <HiChevronLeft className="w-4 h-4" />
                    </button>
                    {getPaginationRange().map((page, idx) =>
                      page === 'dots' ? (
                        <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">...</span>
                      ) : (
                        <button key={page} onClick={() => changePage(page)}
                          className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${page === currentPage ? 'bg-brand text-white shadow-sm hover:opacity-90' : 'text-gray-900 hover:bg-gray-100'}`}>
                          {page}
                        </button>
                      )
                    )}
                    <button onClick={() => changePage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                      <HiChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">{isEditing ? 'Edit Gift Card' : 'Add Gift Card'}</h2>
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              {formGeneralError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{formGeneralError}</div>
              )}

              {/* Title */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => { setFormData({ ...formData, title: e.target.value }); if (formErrors.title) setFormErrors({ ...formErrors, title: '' }); }}
                  placeholder="e.g. Diwali Special Gift Card"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${formErrors.title ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'}`}
                />
                {formErrors.title && <p className="mt-1 text-xs text-red-600">{formErrors.title}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description <span className="text-gray-400">(optional)</span></label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter a description..."
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 resize-none"
                />
              </div>

              {/* Image */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Image <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 group relative">
                      <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
                      <span className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 sm:whitespace-nowrap leading-tight inline-block ${
                        imageSource === 'url' ? 'w-[185px] sm:w-fit sm:max-w-[290px]' : 'w-[175px] sm:w-fit sm:max-w-[240px]'
                      }`}>
                        {imageSource === 'url'
                          ? 'Switch left to choose a file from your device'
                          : 'Switch right to paste an image URL.'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const next = imageSource === 'url' ? 'file' : 'url';
                        setImageSource(next);
                        setImageFile(null);
                        setImageUploadedUrl('');
                        setImageUploadError('');
                        if (formErrors.image) setFormErrors(prev => ({ ...prev, image: '' }));
                        // Restore the original image (existing or last confirmed) when toggling back
                        setFormData(prev => ({ ...prev, image: originalImage }));
                      }}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${imageSource === 'url' ? 'bg-brand' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${imageSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>

                {imageSource === 'url' ? (
                  <div>
                    <input
                      type="url"
                      value={formData.image}
                      onChange={e => {
                        const val = e.target.value;
                        setFormData({ ...formData, image: val });
                        setOriginalImage(val);
                        if (formErrors.image) setFormErrors({ ...formErrors, image: '' });
                      }}
                      placeholder="https://cdn.vedify.app/gift-cards/..."
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-brand focus:border-transparent ${formErrors.image ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formData.image && (
                      <div className="mt-2">
                        <img
                          src={formData.image}
                          alt="Preview"
                          className="w-full h-32 object-cover rounded-lg"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={e => { const file = e.target.files[0]; if (file) handleImageUpload(file); }}
                      disabled={uploadingImage}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${
                        imageUploadError || formErrors.image ? 'border-red-500' : 'border-gray-300'
                      } ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    {uploadingImage && (
                      <div className="mt-2 flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-xs text-gray-600">Uploading image...</span>
                      </div>
                    )}
                    {(imageUploadedUrl || imageFile || formData.image) && !uploadingImage && (
                      <div className="mt-2">
                        <img
                          src={imageUploadedUrl || (imageFile ? URL.createObjectURL(imageFile) : formData.image)}
                          alt="Preview"
                          className="w-full h-32 object-cover rounded-lg"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    {imageUploadError && <p className="mt-1 text-xs text-red-500">{imageUploadError}</p>}
                    {formErrors.image && !imageUploadError && <p className="mt-1 text-xs text-red-500">{formErrors.image}</p>}
                  </div>
                )}
                {formErrors.image && imageSource === 'url' && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.image}</p>
                )}
              </div>

              {/* Status */}
              <div className="flex items-center gap-3">
                <label className="text-xs font-medium text-gray-700">Active</label>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.is_active ? 'bg-brand' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200 flex-shrink-0">
              <button onClick={closeForm} disabled={saving} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button onClick={handleSave} disabled={saving || uploadingImage} className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                {saving ? <><Spinner />{isEditing ? 'Updating...' : 'Adding...'}</> : (isEditing ? 'Update' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">Delete Gift Card</h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">Delete gift card <strong>"{itemToDelete.title}"</strong>? This cannot be undone.</p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <button onClick={closeDelete} disabled={deleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button onClick={confirmDelete} disabled={deleting} className="px-4 py-2 text-sm font-medium text-white bg-[#F76464] rounded-lg disabled:opacity-50 flex items-center gap-2">
                {deleting ? <><Spinner />Deleting...</> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftCards;
